// faq.jsx - FAQ page. Compiled to dist/faq.js by build.mjs.

const { useState, useEffect } = React;
const { Reveal, Aurora } = window;
const { Nav, Footer, DiscordCTA } = window;

const FAQ_ITEMS = [
  {
    q: 'What exactly does PyK7VM do?',
    a: 'It compiles Python source into a hardened native binary (.pyd on Windows, .so on Linux). The output runs inside a custom virtual machine with randomized opcodes, encrypted constants, and active anti-analysis defenses. Your original .py files are never shipped.',
  },
  {
    q: 'Is the beta free to use?',
    a: 'Yes. You can build and download binaries at no cost during the public beta program. No credit card is required. Once paid plans launch, free accounts continue with permanent free tier limits, while early beta users receive a grandfathered 40% discount.',
  },
  {
    q: 'How is this different from PyArmor or Nuitka?',
    a: 'PyArmor encrypts bytecode and decrypts it into CPython memory during runtime. Nuitka compiles to C but preserves Python semantics in a debuggable form. PyK7VM replaces the CPython dispatch loop with a per-build VM whose opcodes, encryption keys, and dispatch structure differ on every build, so a static unpacker written for one binary does not apply to another.',
  },
  {
    q: 'Which Python versions are supported?',
    a: 'PyK7VM supports Python 3.11, 3.12, and 3.13 on Windows and Linux, for both x64 and ARM64 (.pyd / .so / .exe), plus free-threaded Python 3.13t (no-GIL) on Windows x64. ARM64 covers Windows on ARM, Linux AArch64 servers, and Apple Silicon - builds cross-compile from a regular x64 host.',
  },
  {
    q: 'What is the performance overhead?',
    a: 'For typical application code (API endpoints, licensing checks, authentication, data transforms), virtualization overhead is 1.1x–1.8x CPython execution time, with a cold-start latency of 5–20ms. CPU-bound mathematical loops and tensor computations are auto-detected and run at 1.0x native speed.',
  },
  {
    q: 'Can I protect code that uses NumPy, Pandas, Pillow, or SciPy?',
    a: 'Yes. External C-extension packages (NumPy, Pandas, PyTorch, SciPy, Pillow) interact seamlessly with PyK7VM modules. Calls to external C modules run at native speed, while your proprietary orchestration logic remains virtualized inside the VM.',
  },
  {
    q: 'Can I protect a whole Python project?',
    a: 'Yes. Submit a .zip archive of your package. The compiler walks the AST, compiles protected functions into polymorphic bytecode, and emits a single extension binary that imports like a normal Python module.',
  },
  {
    q: 'How big is the resulting binary?',
    a: 'A compact module with basic decoys compiles to approximately 250–350 KB. Full packages with comprehensive decoy farms and anti-tamper traps typically range between 1.5 MB and 2.5 MB.',
  },
  {
    q: 'My application runs in an Azure VM, AWS EC2, or VMware. Will it be blocked?',
    a: 'No. Hypervisor / anti-VM detection is an optional profile setting intended for desktop distribution. By default, cloud virtual machines, EC2 instances, and containerized Docker environments execute normally.',
  },
  {
    q: 'I built for Python 3.11.2. Do I need to rebuild for Python 3.11.5?',
    a: 'No. Python C-API ABI stability guarantees that binaries compiled for a minor version (e.g. Python 3.11.x) run across all patch releases (3.11.0 through 3.11.10+) on that platform without recompilation.',
  },
  {
    q: 'If my protected code raises an exception, can I still read the traceback?',
    a: 'Yes. Unhandled exceptions bubble up through standard Python try/except blocks and preserve original exception types and error messages. Internal line numbers inside virtualized frames are sanitized to prevent source reconstruction.',
  },
  {
    q: 'Are the binaries code-signed?',
    a: 'Binaries produced during the beta are unsigned by default. For commercial distribution on Windows, we recommend signing your final .pyd files with your corporate Authenticode certificate to prevent Microsoft SmartScreen warnings.',
  },
  {
    q: 'What happens to my source code when I upload it?',
    a: 'Source is processed in an ephemeral, isolated build sandbox in France (EU) and deleted immediately after the binary is generated (or within 24h for failed jobs). We never retain your code, store it in logs, or use it for AI model training.',
  },
  {
    q: 'Does it work with async/await and generators?',
    a: 'Yes. The VM has native support for generators (yield, yield from, send(), throw(), close()), async/await coroutines, context managers (with/__enter__/__exit__), closures, default args, *args/**kwargs, and full exception handling.',
  },
  {
    q: 'Can someone still reverse engineer the output?',
    a: 'Yes, with enough effort. Static analysis tools (capstone, angr, Ghidra) get no standard bytecode to reconstruct because the VM instruction set is generated per build and handler code is encrypted on disk, but a skilled adversary with kernel-mode access or hardware tracing can observe runtime execution. No client-side software protection is unbreakable; the goal is to raise reverse-engineering cost above the value of the protected IP.',
  },
  {
    q: 'What is the @pk7vm_export decorator for?',
    a: 'Protected functions are private by default: their names are stripped and the module dict holds only the VM object. @pk7vm_export (or --export name:alias on the CLI) makes a function callable by importers under a public alias, without leaking the internal name as a string anywhere in the binary.',
  },
  {
    q: 'Is there a CLI I can use in CI/CD?',
    a: 'Yes. The REST API (POST /api/v1/job_submit.php → poll /api/v1/job_status.php → GET /api/v1/job_download.php) is designed for automation. See the Docs page for endpoint reference and a Python client example. API key authentication is available for Beta accounts.',
  },
  {
    q: 'What is the beta pricing model?',
    a: 'Free during beta. Free tier includes 5 builds/day (2 MB single .py, free profile). Approved Beta accounts unlock 20 builds/day (10 MB zip, premium profile with dump detection and REST API). Post-beta, premium tiers offer unlimited builds (500 MB max). Early users get a 40% lifetime discount.',
  },
  {
    q: 'Do you offer a self-hosted or on-premise build?',
    a: 'A self-hosted offline compiler is on the product roadmap for defense contractors, financial institutions, and sovereign teams requiring 100% offline compilation with zero external connectivity. It is not available yet. Contact sales@pyk7vm.com to be notified when it launches.',
  },
  {
    q: 'Do you plan to support other languages?',
    a: 'Python is our dedicated focus. The VM and pipeline are Python-specific (AST mutation, CPython bytecode compilation, .pyd/.so output). No other language targets are planned for the near term.',
  },
];

function AccordionItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '20px 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          minHeight: 48,
        }}
      >
        <span style={{ fontSize: 'clamp(15px, 2vw, 17px)', fontWeight: 500, color: open ? 'var(--text)' : 'var(--text-dim)', letterSpacing: -0.3, lineHeight: 1.35, transition: 'color .2s' }}>
          {item.q}
        </span>
        <span style={{ flexShrink: 0, width: 26, height: 26, borderRadius: '50%', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 16, transition: 'transform .25s, border-color .2s', transform: open ? 'rotate(45deg)' : 'none', borderColor: open ? 'var(--brand)' : undefined, marginTop: 1 }}>
          +
        </span>
      </button>
      <div style={{ overflow: 'hidden', maxHeight: open ? 500 : 0, opacity: open ? 1 : 0, transition: 'max-height .35s cubic-bezier(.4,0,.2,1), opacity .25s' }}>
        <p style={{ paddingBottom: 22, color: 'var(--text-dim)', fontSize: 'clamp(13.5px, 1.8vw, 15px)', lineHeight: 1.75, maxWidth: 760, margin: 0 }}>
          {item.a}
        </p>
      </div>
    </div>
  );
}

function FAQHero() {
  return (
    <section style={{ position: 'relative', padding: 'clamp(96px, 12vw, 150px) clamp(16px, 4vw, 32px) clamp(40px, 6vw, 80px)', textAlign: 'center', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', inset: 0, opacity: 0.35, pointerEvents: 'none' }}>
        <Aurora palette={{ a: '#1e40af', b: '#3b82f6', c: '#0a0a0f' }} />
      </div>
      <div style={{ position: 'relative', zIndex: 1, maxWidth: 720, margin: '0 auto' }}>
        <Reveal>
          <h1 style={{ fontSize: 'clamp(34px, 5.5vw, 72px)', fontWeight: 500, letterSpacing: -2, lineHeight: 1.06, color: 'var(--text)' }}>
            Common questions.
          </h1>
        </Reveal>
        <Reveal delay={80}>
          <p style={{ marginTop: 16, color: 'var(--text-dim)', fontSize: 'clamp(14.5px, 2vw, 16.5px)', lineHeight: 1.7 }}>
            If you don't find your answer here, ask on{' '}
            <a href="https://discord.gg/hpx6XkqmKM" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-soft)', textDecoration: 'underline' }}>Discord</a>.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section style={{ borderTop: '1px solid var(--border)', padding: 'clamp(50px, 7vw, 80px) clamp(16px, 4vw, 32px) clamp(60px, 8vw, 120px)' }}>
      <div style={{ maxWidth: 840, margin: '0 auto' }}>
        {FAQ_ITEMS.map((item, i) => (
          <AccordionItem key={i} item={item} />
        ))}
      </div>
    </section>
  );
}

function FAQApp() {
  useEffect(() => {
    if (typeof window.Lenis !== 'undefined') {
      const lenis = new window.Lenis({
        duration: 0.8,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        touchMultiplier: 1.5,
      });
      window.__lenis = lenis;

      let rafId;
      function raf(time) {
        lenis.raf(time);
        rafId = requestAnimationFrame(raf);
      }
      rafId = requestAnimationFrame(raf);

      return () => {
        cancelAnimationFrame(rafId);
        lenis.destroy();
      };
    }
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: '100vw', overflowX: 'hidden' }}>
      <Nav />
      <FAQHero />
      <FAQSection />
      <Footer />
      <DiscordCTA />
    </div>
  );
}

ReactDOM.hydrateRoot(document.getElementById('root'), React.createElement(FAQApp));
